import React from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  Collapse,
  LinearProgress,
  Chip,
  Grid,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  TrendingUp as ProgressIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CompleteIcon,
  FitnessCenter as TrainingIcon,
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
  Assignment as WorkoutIcon,
  List as ListIcon,
  Timeline as TimelineIcon,
  ShowChart as ShowChartIcon,
} from "@mui/icons-material";

import {
  StyledTrainingCard,
  TrainingHeader,
  TrainingContent,
} from "./StyledComponents";
import NewColors from "../../../../../../themes/NewColors";
import ExerciseDetail from "./ExerciseDetail";
import WorkoutChart from "./WorkoutChart";
import { useExercisesBigQuery } from "../../../../../../hooks/useExercisesBigQuery";

const RecentWorkouts = ({
  workoutSessions,
  chartType,
  setChartType,
  expandedWorkouts,
  toggleWorkoutExpansion,
}) => {
  // Fetch exercises from BigQuery for name lookups
  const { data: exercisesData, isLoading: exercisesLoading } =
    useExercisesBigQuery({
      search: "",
      type: "all",
      limit: 1000,
      offset: 0,
      enabled: true,
    });

  // Extract exercises array from BigQuery response
  const bigQueryExercises = exercisesData?.data?.exercises || [];

  // Helper function to get exercise name by ID from BigQuery data
  const getExerciseNameById = React.useMemo(() => {
    return (exerciseId) => {
      console.log("🔍 RecentWorkouts - Looking up exercise:", {
        exerciseId,
        bigQueryExercisesCount: bigQueryExercises.length,
        firstFewExercises: bigQueryExercises
          .slice(0, 3)
          .map((ex) => ({ id: ex.id, name: ex.name })),
        exercisesLoading,
      });

      if (!exerciseId) return "Unknown Exercise";

      // First try to find in BigQuery exercises
      const bigQueryExercise = bigQueryExercises.find(
        (ex) => ex.id === exerciseId,
      );
      if (bigQueryExercise) {
        console.log("✅ Found exercise in BigQuery:", bigQueryExercise.name);
        return bigQueryExercise.name;
      }

      console.log("❌ Exercise not found in BigQuery, using formatted name");
      // Last resort - format the ID as a readable name
      const formattedName = exerciseId
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      return formattedName;
    };
  }, [bigQueryExercises, exercisesLoading]);
  return (
    <StyledTrainingCard>
      <TrainingHeader>
        <Stack spacing={0.25}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ProgressIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Workout history
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sessions for the selected month — switch to Chart View for progress graphs
              </Typography>
            </Box>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant={chartType === "list" ? "contained" : "outlined"}
            size="small"
            onClick={() => setChartType("list")}
            startIcon={<ListIcon />}
            sx={{
              minWidth: 120,
              "&.MuiButton-contained": {
                bgcolor: "primary.main",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              },
            }}
          >
            List View
          </Button>
          <Button
            variant={chartType === "chart" ? "contained" : "outlined"}
            size="small"
            onClick={() => setChartType("chart")}
            startIcon={<TimelineIcon />}
            sx={{
              minWidth: 120,
              "&.MuiButton-contained": {
                bgcolor: "primary.main",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              },
            }}
          >
            Chart View
          </Button>
        </Stack>
      </TrainingHeader>
      <TrainingContent>
        {chartType === "list" ? (
          // List View
          workoutSessions.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& .MuiTableCell-head": {
                      fontWeight: 700,
                      fontSize: "0.875rem",
                    },
                  }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2">Date & Status</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TrainingIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2">
                        Exercise Overview
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ProgressIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2">Sets Progress</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      justifyContent="center"
                    >
                      <CompleteIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2">Completion</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="subtitle2">Actions</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workoutSessions.map((workout) => (
                  <React.Fragment key={workout.id}>
                    <TableRow sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {(() => {
                              const workoutDate = new Date(workout.date);
                              const isValidDate = !isNaN(workoutDate.getTime());
                              return isValidDate
                                ? workoutDate.toLocaleDateString("en-US", {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                  })
                                : "Invalid Date";
                            })()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {workout.completed ? "Completed" : "In Progress"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: workout.completed
                                ? "success.light"
                                : "warning.light",
                            }}
                          >
                            <TrainingIcon fontSize="small" />
                            {/* <Badge
                              badgeContent={workout.exercises.length}
                              color={workout.completed ? "success" : "warning"}
                            >
                              <TrainingIcon fontSize="small" />
                            </Badge> */}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ mb: 0.5 }}
                            >
                              <Typography variant="body2" fontWeight="bold">
                                {workout.exercises.length} exercises
                              </Typography>
                              <Chip
                                label={`${workout.exercises.filter((e) => e.completed).length}/${workout.exercises.length} done`}
                                size="small"
                                color={
                                  workout.exercises.filter((e) => e.completed)
                                    .length === workout.exercises.length
                                    ? "success"
                                    : "info"
                                }
                                variant="outlined"
                              />
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {workout.exercises
                                .map((e) => {
                                  const name = exercisesLoading
                                    ? "Loading..."
                                    : e.exerciseName &&
                                        !e.exerciseName.match(
                                          /^[A-Za-z0-9]{20,}$/,
                                        )
                                      ? e.exerciseName
                                      : getExerciseNameById(
                                          e.exercise || e.exerciseId || e.id,
                                        );
                                  return e.clientAdded === true
                                    ? `${name} (Added)`
                                    : name;
                                })
                                .slice(0, 2)
                                .join(", ")}
                              {workout.exercises.length > 2 &&
                                ` +${workout.exercises.length - 2} more`}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={
                                (workout.completedSets / workout.totalSets) *
                                100
                              }
                              sx={{
                                flex: 1,
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: NewColors.sectionBackground,
                                "& .MuiLinearProgress-bar": {
                                  background: workout.completed
                                    ? `linear-gradient(45deg, ${NewColors.status.success} 30%, ${NewColors.status.success} 90%)`
                                    : `linear-gradient(45deg, ${NewColors.status.warning} 30%, ${NewColors.status.warning} 90%)`,
                                },
                              }}
                            />
                            <Typography variant="caption" fontWeight="bold">
                              {workout.completedSets}/{workout.totalSets}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={`${((workout.completedSets / workout.totalSets) * 100).toFixed(0)}% complete`}
                              size="small"
                              variant="outlined"
                              color={workout.completed ? "success" : "info"}
                            />
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Stack spacing={1} alignItems="center">
                          <Chip
                            label={
                              workout.completed ? "Completed" : "In Progress"
                            }
                            color={workout.completed ? "success" : "warning"}
                            size="small"
                            icon={
                              workout.completed ? (
                                <CompleteIcon />
                              ) : (
                                <TimerIcon />
                              )
                            }
                          />
                          {!workout.completed && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {
                                workout.exercises.filter((e) => !e.completed)
                                  .length
                              }{" "}
                              remaining
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => toggleWorkoutExpansion(workout.id)}
                            sx={{
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.1),
                              "&:hover": {
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                          >
                            {expandedWorkouts.has(workout.id) ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.1),
                              "&:hover": {
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                            title="View Workout Details"
                          >
                            <WorkoutIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Expandable Exercise Details */}
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, border: "none" }}>
                        <Collapse in={expandedWorkouts.has(workout.id)}>
                          <Box sx={{ p: 2, bgcolor: "grey.50" }}>
                            <Typography
                              variant="h6"
                              sx={{ mb: 2, fontWeight: 600 }}
                            >
                              Exercise Details
                            </Typography>

                            <Grid container spacing={2}>
                              {workout.exercises.map((exercise) => (
                                <Grid item xs={12} md={6} key={exercise.id}>
                                  <ExerciseDetail exercise={exercise} />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "grey.50",
                borderStyle: "dashed",
                borderWidth: 2,
                borderColor: "grey.300",
              }}
            >
              <TrainingIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                No recent workouts found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Waiting for client to start exercising
              </Typography>
            </Paper>
          )
        ) : // Chart View
        workoutSessions.length > 0 ? (
          <WorkoutChart workoutSessions={workoutSessions} />
        ) : (
          <Paper
            sx={{
              p: 3,
              textAlign: "center",
              bgcolor: "grey.50",
              borderStyle: "dashed",
              borderWidth: 2,
              borderColor: "grey.300",
            }}
          >
            <ShowChartIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No data to visualize
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Charts will appear when client starts exercising
            </Typography>
          </Paper>
        )}
      </TrainingContent>
    </StyledTrainingCard>
  );
};

export default RecentWorkouts;
